import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Chip,
  Collapse,
  IconButton
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import axios from 'axios';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: theme.spacing(3),
    minWidth: 500
  },
  fieldGroup: {
    marginBottom: theme.spacing(2)
  },
  timeFields: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center'
  },
  notesField: {
    marginTop: theme.spacing(2)
  },
  chip: {
    margin: theme.spacing(0.5)
  },
  collapsibleHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    cursor: 'pointer'
  },
  collapsibleTitle: {
    fontWeight: 500
  }
}));

const defaultAccessDays = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: true,
  sunday: true
};

const getContactName = (contact = {}) => (
  [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() ||
  contact.name ||
  contact.displayName ||
  contact.email ||
  ''
);

const getContactPhone = (contact = {}) => (
  contact.phoneNumbers?.[0]?.number ||
  contact.phone ||
  contact.phoneNumber ||
  contact.mobileNumber ||
  ''
);

const getContactEmail = (contact = {}) => contact.email || '';

const getClientId = (client) => {
  if (!client) return '';
  if (typeof client === 'string') return client;
  return client._id || client.id || '';
};

const normalizeContactForSave = (contact = {}) => {
  const normalized = {
    contactName: getContactName(contact),
    contactPhone: getContactPhone(contact),
    contactEmail: getContactEmail(contact)
  };

  const contactId = contact._id || contact.contactId;
  if (contactId) normalized.contact = contactId;

  return normalized;
};

const getLegacyContacts = (restrictionDetails = {}) => {
  if (Array.isArray(restrictionDetails.contacts) && restrictionDetails.contacts.length) {
    return restrictionDetails.contacts.map(contact => ({
      _id: contact.contact || contact._id,
      contactId: contact.contact || contact.contactId,
      firstName: contact.firstName,
      lastName: contact.lastName,
      name: contact.contactName || contact.name,
      email: contact.contactEmail || contact.email,
      phoneNumbers: contact.contactPhone ? [{ number: contact.contactPhone }] : contact.phoneNumbers,
      phone: contact.phone
    }));
  }

  if (!restrictionDetails.contactName) return [];

  const nameParts = restrictionDetails.contactName.split(' ');
  return [{
    firstName: nameParts[0] || restrictionDetails.contactName,
    lastName: nameParts.slice(1).join(' ') || '',
    name: restrictionDetails.contactName,
    phoneNumbers: restrictionDetails.contactPhone ? [{ number: restrictionDetails.contactPhone }] : []
  }];
};

const RestrictedAreaDialog = ({ open, onClose, onSave, editingArea = null, url, clientId }) => {
  const classes = useStyles();
  const resolvedClientId = getClientId(clientId) || getClientId(editingArea?.client);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [accessHoursOpen, setAccessHoursOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    restrictionDetails: {
      contactName: '',
      contactPhone: '',
      contacts: [],
      keyRequired: false,
      accessCode: '',
      accessHours: {
        start: '',
        end: ''
      },
      accessDays: defaultAccessDays,
      accessNotes: '',
      restrictionType: 'appointment'
    },
    paths: []
  });

  // Fetch contacts when dialog opens
  useEffect(() => {
    const fetchContacts = async () => {
      if (open && resolvedClientId && url) {
        setLoadingContacts(true);
        try {
          const response = await axios.get(`${url}/api/customer/getContacts`, {
            params: { id: resolvedClientId }
          });

          // Handle different response formats
          const data = response.data || {};
          const contactList = Array.isArray(data)
            ? data
            : (data.contacts || data.data || []);

          setContacts(contactList);
        } catch (error) {
          console.error('Error fetching contacts:', error);
          setContacts([]);
        } finally {
          setLoadingContacts(false);
        }
      } else if (open && !resolvedClientId) {
        console.warn('No client ID provided to fetch contacts');
        setContacts([]);
      }
    };

    fetchContacts();
  }, [open, resolvedClientId, url]);

  useEffect(() => {
    if (editingArea) {
      const legacyContacts = getLegacyContacts(editingArea.restrictionDetails);
      const hasAccessHours = Boolean(
        editingArea.restrictionDetails?.accessHours?.start ||
        editingArea.restrictionDetails?.accessHours?.end ||
        ['time-based', 'open-hours'].includes(editingArea.restrictionDetails?.restrictionType)
      );
      setFormData({
        name: editingArea.name || '',
        restrictionDetails: {
          contactName: editingArea.restrictionDetails?.contactName || '',
          contactPhone: editingArea.restrictionDetails?.contactPhone || '',
          contacts: editingArea.restrictionDetails?.contacts || [],
          keyRequired: editingArea.restrictionDetails?.keyRequired || false,
          accessCode: editingArea.restrictionDetails?.accessCode || '',
          accessHours: {
            start: editingArea.restrictionDetails?.accessHours?.start || '',
            end: editingArea.restrictionDetails?.accessHours?.end || ''
          },
          accessDays: editingArea.restrictionDetails?.accessDays || defaultAccessDays,
          restrictionType: editingArea.restrictionDetails?.restrictionType || 'appointment',
          accessNotes: editingArea.restrictionDetails?.accessNotes || ''
        }
      });
      setAccessHoursOpen(hasAccessHours);

      const selected = legacyContacts.map(savedContact => {
        const savedId = savedContact.contactId || savedContact._id;
        const savedName = getContactName(savedContact);
        return contacts.find(contact => (
          (savedId && String(contact._id) === String(savedId)) ||
          getContactName(contact) === savedName
        )) || savedContact;
      });
      setSelectedContacts(selected);
    } else {
      // Reset form for new area
      setFormData({
        name: '',
        restrictionDetails: {
          contactName: '',
          contactPhone: '',
          contacts: [],
          keyRequired: false,
          accessCode: '',
          accessHours: {
            start: '',
            end: ''
          },
          accessDays: defaultAccessDays,
          accessNotes: '',
          restrictionType: 'appointment'
        },
        paths: []
      });
      setSelectedContacts([]);
      setAccessHoursOpen(false);
    }
  }, [editingArea, open, contacts]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleDetailsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      restrictionDetails: {
        ...prev.restrictionDetails,
        [field]: value
      }
    }));
  };

  const handleRestrictionTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      restrictionDetails: {
        ...prev.restrictionDetails,
        restrictionType: value,
        keyRequired: value === 'key-required'
      }
    }));
  };

  const handleContactsChange = (newContacts) => {
    const selected = newContacts || [];
    const primaryContact = selected[0];

    setSelectedContacts(selected);
    setFormData(prev => ({
      ...prev,
      restrictionDetails: {
        ...prev.restrictionDetails,
        contacts: selected.map(normalizeContactForSave),
        contactName: primaryContact ? getContactName(primaryContact) : '',
        contactPhone: primaryContact ? getContactPhone(primaryContact) : ''
      }
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      restrictionDetails: {
        ...prev.restrictionDetails,
        accessDays: {
          ...prev.restrictionDetails.accessDays,
          [day]: !prev.restrictionDetails.accessDays[day]
        }
      }
    }));
  };

  const handleSubmit = () => {
    const selected = selectedContacts || [];
    const primaryContact = selected[0];
    onSave({
      ...formData,
      restrictionDetails: {
        ...formData.restrictionDetails,
        contacts: selected.map(normalizeContactForSave),
        contactName: primaryContact ? getContactName(primaryContact) : '',
        contactPhone: primaryContact ? getContactPhone(primaryContact) : ''
      }
    });
    onClose();
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingArea ? 'Edit Restricted Area' : 'Add Restricted Area Details'}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2}>
          {/* Area Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Area Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Pool Equipment Area, Backyard Gate"
              variant="outlined"
              required
            />
          </Grid>

          {/* Restriction Type */}
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Restriction Level</InputLabel>
              <Select
                value={formData.restrictionDetails.restrictionType}
                onChange={(e) => handleRestrictionTypeChange(e.target.value)}
                label="Restriction Level"
              >
                <MenuItem value="appointment">Restricted (Appointment Required)</MenuItem>
                <MenuItem value="open-hours">Open Hours</MenuItem>
                <MenuItem value="time-based">Time Based (Request Access On-Site)</MenuItem>
                <MenuItem value="guard-attendant">See Guard/Attendant</MenuItem>
                <MenuItem value="code-required">Code Required</MenuItem>
                <MenuItem value="key-required">Key Required</MenuItem>
                <MenuItem value="no-access">No Access</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {formData.restrictionDetails.restrictionType === 'code-required' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Access Code"
                value={formData.restrictionDetails.accessCode}
                onChange={(e) => handleDetailsChange('accessCode', e.target.value)}
                placeholder="Enter gate/access code"
                variant="outlined"
              />
            </Grid>
          )}

          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Contact Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              value={selectedContacts}
              onChange={(event, newValue) => handleContactsChange(newValue)}
              options={contacts}
              disableCloseOnSelect
              getOptionSelected={(option, value) => {
                if (option._id && (value._id || value.contactId)) {
                  return String(option._id) === String(value._id || value.contactId);
                }
                return getContactName(option) === getContactName(value);
              }}
              getOptionLabel={(option) => getContactName(option)}
              renderTags={(value, getTagProps) => (
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option._id || option.contactId || `${getContactName(option)}-${index}`}
                    label={getContactName(option)}
                    className={classes.chip}
                  />
                ))
              )}
              renderOption={(option, { selected }) => {
                const phoneNumber = getContactPhone(option);
                const email = getContactEmail(option);
                return (
                  <Box display="flex" alignItems="center" width="100%">
                    <Checkbox
                      checked={selected}
                      color="primary"
                      style={{ marginRight: 8 }}
                    />
                    <Box>
                      <Typography variant="body1">{getContactName(option)}</Typography>
                      {(phoneNumber || email) && (
                      <Typography variant="caption" color="textSecondary">
                        {[phoneNumber, email].filter(Boolean).join(' • ')}
                      </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              loading={loadingContacts}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Contacts"
                  variant="outlined"
                  placeholder="Choose contacts..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {selectedContacts.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Primary Contact: {getContactName(selectedContacts[0])}
                {getContactPhone(selectedContacts[0]) && ` - ${getContactPhone(selectedContacts[0])}`}
              </Typography>
            </Grid>
          )}

          {/* Access Hours */}
          <Grid item xs={12}>
            <Box
              className={classes.collapsibleHeader}
              onClick={() => setAccessHoursOpen(prev => !prev)}
            >
              <Box>
                <Typography variant="subtitle1" className={classes.collapsibleTitle}>
                  Access Hours
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Optional days and time window for when crews can enter this area
                </Typography>
              </Box>
              <IconButton size="small">
                {accessHoursOpen ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={accessHoursOpen}>
              <Box pt={1}>
                <div className={classes.timeFields}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={formData.restrictionDetails.accessHours.start}
                    onChange={(e) => handleDetailsChange('accessHours', {
                      ...formData.restrictionDetails.accessHours,
                      start: e.target.value
                    })}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                  <Typography>to</Typography>
                  <TextField
                    label="End Time"
                    type="time"
                    value={formData.restrictionDetails.accessHours.end}
                    onChange={(e) => handleDetailsChange('accessHours', {
                      ...formData.restrictionDetails.accessHours,
                      end: e.target.value
                    })}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </div>
              </Box>

              {/* Access Days */}
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Access Days
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {Object.entries(formData.restrictionDetails.accessDays).map(([day, checked]) => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => handleDayToggle(day)}
                          size="small"
                          color="primary"
                        />
                      }
                      label={day.charAt(0).toUpperCase() + day.slice(1)}
                    />
                  ))}
                </Box>
              </Box>
            </Collapse>
          </Grid>

          {/* Notes */}
          <Grid item xs={12} className={classes.notesField}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Access Notes / Special Instructions"
              value={formData.restrictionDetails.accessNotes}
              onChange={(e) => handleDetailsChange('accessNotes', e.target.value)}
              placeholder="e.g., Call 30 minutes before arrival, Dog in backyard, Gate code is #1234"
              variant="outlined"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!formData.name}
        >
          {editingArea ? 'Update' : 'Save'} Restricted Area
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RestrictedAreaDialog;
